/**
 * Designer branding (Phase 18).
 *
 * The app ships with built-in branding and a designer can replace it. Every
 * surface that renders it needs the same fallback rules, so they live here
 * rather than being re-decided per template — a half-set profile (a company
 * name but no tagline) must not produce a header with a dangling separator or
 * an empty line.
 */

import { PRODUCT_NAME, monogramFor } from '@/lib/host-branding';

/**
 * The last-resort name — the PRODUCT's, never a firm's. Until Phase 27 this
 * was "Tonie Searle Designs", so a firm that hadn't saved branding would have
 * shown Tonie's name to its own clients.
 */
export const DEFAULT_COMPANY = PRODUCT_NAME;
export const DEFAULT_TAGLINE = 'Project Hub';

export interface Branding {
  company_name?: string | null;
  tagline?: string | null;
  logo_storage_path?: string | null;
  /** The firm's own name (0037) — the fallback before the product's. */
  firm_name?: string | null;
  /** The designer's photo (0045). Absent everywhere a face doesn't belong. */
  photo_storage_path?: string | null;
  /** A key from BRAND_FONTS (0045), not a font name. */
  brand_font?: string | null;
  /** #RRGGBB for the company name (0045). */
  brand_color?: string | null;
}

/**
 * Company name to display. Never empty. Order: the saved company name, the
 * firm's own name (from the branding itself, or `firmName` when the caller
 * knows it), then the product's name.
 */
export function companyName(b: Branding | null | undefined, firmName?: string | null): string {
  return b?.company_name?.trim() || b?.firm_name?.trim() || firmName?.trim() || DEFAULT_COMPANY;
}

/**
 * Tagline to display, or '' when there is none.
 *
 * Empty string rather than the default: a designer who deliberately clears
 * their tagline should get no tagline, not ours. Only an ABSENT profile falls
 * back — `undefined` means "never set up", `''` means "set to nothing".
 */
export function tagline(b: Branding | null | undefined): string {
  if (b?.tagline == null) return b ? '' : DEFAULT_TAGLINE;
  return b.tagline.trim();
}

/** Storage path of the logo, or null when the built-in mark should be used. */
export function logoPath(b: Branding | null | undefined): string | null {
  const p = b?.logo_storage_path?.trim();
  return p ? p : null;
}

/** Storage path of the designer's photo (0045), or null when there isn't one. */
export function photoPath(b: Branding | null | undefined): string | null {
  const p = b?.photo_storage_path?.trim();
  return p ? p : null;
}

/**
 * Initials for the fallback mark, derived from the displayed company name.
 *
 * Delegates to `monogramFor` rather than repeating the rule: this copy used to
 * take the first CHARACTER of the second word, so "Smith & Jones Design" was
 * drawn as "S&" and "Åse & Co" as "Å&" — while the sign-in page, which already
 * used monogramFor, got them right (2026-09-30).
 */
export function brandInitials(b: Branding | null | undefined, firmName?: string | null): string {
  return monogramFor(companyName(b, firmName));
}

